/**
 * The shoot's coordinates, recovered from the property detail when the store
 * has none.
 *
 * The store SHOULD carry them — `PropertySelectionDrawer` puts them there when
 * the client picks a property. Two ways it does not:
 *
 *   - the client pressed "Next step" before the detail request came back, or it
 *     errored. "Next step" stays enabled in both cases on purpose, so a slow
 *     lookup cannot trap them — but the coordinates ride on that same request,
 *     so they are simply absent, and nothing wrote them afterwards;
 *   - the draft predates the store carrying coordinates at all.
 *
 * Either way the instruction-options POST omits them, the backend answers 422
 * "Coordinates are required for this category", and the Instructions step is
 * dead with no way to fix a property from there. Recovering them here is what
 * makes that unreachable rather than merely unlikely.
 *
 * The fetch is free in the common case: `usePropertyCompleteness` already asked
 * for the same detail under the same react-query key, so a client who came
 * through the drawer hits the cache. It is skipped entirely once the store has
 * a routable pair.
 */

import { useGetPropertyDetail } from 'dataQueries/property.query'
import type { PropertyRef } from 'models/purchaseFlowRevamp'

import { isRoutable } from './usePropertyCompleteness'

export interface ResolvedPropertyCoordinates {
  /** First ROUTABLE of the property's own pin and its routing pin, or undefined. */
  coordinates?: { lat: number, lng: number }
  /** True while the recovery fetch is in flight — the caller must not send the
   *  request yet, or it sends the very omission this exists to prevent. */
  isRecovering: boolean
}

export function useResolvedPropertyCoordinates(
  property: PropertyRef | null | undefined,
  workspaceId: string | null | undefined
): ResolvedPropertyCoordinates {
  // First ROUTABLE of the two, not merely the first present: a property carrying
  // a stored (0,0) alongside a real routing point would otherwise send the null
  // island and lose the timezone.
  const stored = [property?.coordinates, property?.routingCoordinates].find(isRoutable)

  // `asClient`: the workspace-scoped property endpoints serve the client
  // representation, so an admin's own mimetype 406s. Same options
  // `usePropertyCompleteness` passes, which is what makes this share its cache.
  const needsRecovery = !stored && !!property?.id && !!workspaceId
  const detail = useGetPropertyDetail(
    needsRecovery ? property?.id ?? null : null,
    needsRecovery ? workspaceId ?? null : null,
    true,
    { asClient: true }
  )

  if (stored) return { coordinates: stored, isRecovering: false }
  if (!needsRecovery) return { coordinates: undefined, isRecovering: false }

  const recovered = [
    detail.data?.address?.coordinates,
    detail.data?.creativeRoutingAddress?.coordinates,
  ].find(isRoutable)

  return {
    coordinates: recovered,
    // A failed recovery is not "still recovering": the request would 422, but
    // holding it forever would leave the step spinning with no explanation.
    isRecovering: detail.isLoading,
  }
}
